import { useState, useCallback } from 'react';
import { toast as sonnerToast } from "sonner";

/**
 * アプリ共通の確認ダイアログ（Sonner）と背景グレー化を制御するカスタムフック
 */
export const useAppDialog = () => {
  // ダイアログ表示時の背景グレー（オーバーレイ）の状態管理
  const [isOverlayActive, setIsOverlayActive] = useState(false);

  /**
   * 確認ダイアログを表示する
   * @param title - ダイアログ左上の小タイトル
   * @param message - メインの確認メッセージ
   * @param onConfirm - OKボタン押下時の実行処理
   */
  const showConfirm = useCallback(({ title, message, onConfirm }: { title: string, message: string, onConfirm: () => void }) => {
    // 背景をグレーにする
    setIsOverlayActive(true);

    sonnerToast(
      <div className="w-full text-left">
        {/* 左上のタイトル的文言：視認性を高めるため小さめのフォントに設定 */}
        <div className="text-[12px] text-slate-800 mb-1 leading-none">
          {title}
        </div>
        {/* メインメッセージ：太字で強調 */}
        <div className="text-[18px] font-bold text-slate-800 leading-snug">
          {message}
        </div>
      </div>,
      {
        duration: Infinity, // ユーザーが操作するまで閉じない
        position: 'top-center',
        // Sonnerの枠自体の設定：!blockを指定することで、標準の横並びレイアウトを縦並びに矯正
        className: "w-[calc(100vw-2rem)] md:w-[500px] !p-6 !block",
        action: (
          <div className="flex justify-end gap-3 mt-5 w-full">
            {/* OKボタン */}
            <button
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 min-w-[100px] rounded-md font-bold text-[15px] transition-colors shadow-sm"
              onClick={() => {
                setIsOverlayActive(false); // 背景を元に戻す
                onConfirm();               // 渡された処理を実行
                sonnerToast.dismiss();     // ダイアログを閉じる
              }}
            >
              OK
            </button>
            {/* キャンセルボタン */}
            <button
              className="bg-white hover:bg-slate-50 text-slate-700 px-5 py-2 min-w-[100px] rounded-md font-bold text-[15px] border border-slate-300 transition-colors"
              onClick={() => {
                setIsOverlayActive(false); // 背景を元に戻す
                sonnerToast.dismiss();     // ダイアログを閉じる
              }}
            >
              キャンセル
            </button>
          </div>
        ),
        // ユーザーがエスケープキーや外側クリックで閉じた場合も背景を元に戻す
        onDismiss: () => setIsOverlayActive(false),
      }
    );
  }, []);

  /**
   * 背景を暗くする要素
   * ダイアログ(zIndex 99999)よりも背面に配置するため、zIndex: 8000 を設定
   */
  const OverlayElement = isOverlayActive ? (
    <div className="fixed inset-0 bg-black/40 z-[8000] pointer-events-auto" />
  ) : null;

  return { showConfirm, OverlayElement, setIsOverlayActive };
};